import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box, Stack, Typography, Skeleton, IconButton, Tooltip } from "@mui/material";
import { alpha, keyframes, useTheme } from "@mui/material/styles";
import { Calendar, Inbox, Plus } from "lucide-react";
import {
  draggable,
  dropTargetForElements,
  monitorForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import {
  autoScrollForElements,
  autoScrollWindowForElements,
} from "@atlaskit/pragmatic-drag-and-drop-auto-scroll/element";
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";

import { Avatar } from "../../../components/Avatar";
import { EmptyStateMessage } from "../../../components/EmptyStateMessage";
import SuccessPopup from "../../../components/SuccessPopup";
import { kanbanColors } from "../../../theme/theme";
import { getData } from "../../../components/hooks/getData";
import { useAuth } from "../../../components/contexts/AuthContext";
import { getDropPosition, mountDragPreview } from "../../../utils/drag_drop/dragDropUtils";
import { useViewportBottomHeight } from "../../../hooks/useViewportBottomHeight";
import {
  CLIENTIFY_DEALS_ENDPOINT,
  DEAL_STATUS,
  clientifyErrorMessage,
  updateClientifyDeal,
} from "../../../services/clientifyService";

// Mapeo de filtros a status de Clientify API
const STATUS_MAP = {
  all: null,
  open: DEAL_STATUS.open,
  overdue: DEAL_STATUS.expired,
  won: DEAL_STATUS.won,
  lost: DEAL_STATUS.lost,
};
const DEALS_PAGE_SIZE = 10;
// Mismo lenguaje visual que el kanban de proyectos
const KANBAN_COLUMN_BG = kanbanColors.columnSurface;
const KANBAN_BOTTOM_GAP = 40;
const DRAG_TYPE = "clientify-deal";
const isDealDrag = ({ source }) => source.data?.type === DRAG_TYPE;

// Helper function to get color from stage position
const getColorFromPosition = (position) => {
  const colors = [
    kanbanColors.columns.contacto_inicial,
    kanbanColors.columns.reunion_preventa,
    kanbanColors.columns.envio_presupuesto,
    kanbanColors.columns.conversion_cliente,
    kanbanColors.columns.ganada,
    kanbanColors.columns.perdida,
  ];
  return colors[position % colors.length] || colors[0];
};

const parseNumericAmount = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getCurrencySymbol = (currencyCode) => {
  const normalized = (currencyCode || "EUR").toUpperCase();
  return normalized === "EUR" ? "€" : normalized;
};

const formatAmount = (value, currencyCode = "EUR") => {
  const symbol = getCurrencySymbol(currencyCode);
  return `${parseNumericAmount(value).toLocaleString("es-ES", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${symbol}`;
};

const placeholderGrow = keyframes`
  from { max-height: 0; opacity: 0; }
  to { max-height: 400px; opacity: 1; }
`;

/** Where a drop would land: the column and the slot among its cards. */
const hoverFromLocation = (location) => {
  const [inner] = location.current.dropTargets;
  if (!inner?.data?.columnKey) return null;
  const { columnKey, index, element, count } = inner.data;
  // Over the column but not a card (the gap, or the open slot itself).
  if (index === undefined) return { columnKey, index: count ?? 0, overColumn: true };
  const position = getDropPosition({ input: location.current.input, element });
  return { columnKey, index: position === "before" ? index : index + 1 };
};

const isDraggableDeal = (deal) =>
  [DEAL_STATUS.open, DEAL_STATUS.expired].includes(String(deal?.status));

const metaTextSx = {
  fontSize: "0.75rem",
  color: "text.secondary",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

// Avatar + nombre en una línea (propietario)
const NameRow = ({ name, picture }) => {
  if (!name) return null;

  return (
    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0, flex: "1 1 120px" }}>
      <Avatar size="sm" name={name} src={picture || undefined} />
      <Typography sx={{ ...metaTextSx, color: "text.primary" }}>{name}</Typography>
    </Stack>
  );
};

const formatDate = (dateString) => {
  if (!dateString) return null;
  return new Date(dateString).toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// Tarjeta compacta (3 líneas), como la de proyectos: título, propietario con la fecha
// de cierre a la derecha, y contacto/empresa con el importe a la derecha.
const OpportunityCard = ({ deal, columnKey, index, accent, onOpen, pending, hidden }) => {
  const theme = useTheme();
  const cardRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const canDrag = isDraggableDeal(deal) && !pending;
  const rawAmount = deal.amount ?? deal.value;
  const parsedAmount = Number.parseFloat(rawAmount);
  const hasAmount = Number.isFinite(parsedAmount);
  const isExpired = String(deal.status) === DEAL_STATUS.expired;
  const contactLine = [deal.contact_name, deal.company_name].filter(Boolean).join(" · ");

  useEffect(() => {
    const element = cardRef.current;
    if (!element || !canDrag) return undefined;

    return draggable({
      element,
      getInitialData: () => ({ type: DRAG_TYPE, deal, columnKey }),
      onGenerateDragPreview: ({ nativeSetDragImage, location, source }) => {
        mountDragPreview({
          nativeSetDragImage,
          element: source.element,
          input: location.current.input,
        });
      },
      onDragStart: () => setIsDragging(true),
      onDrop: () => setIsDragging(false),
    });
  }, [canDrag, deal, columnKey]);

  // Each card is a target too, so the drop slot follows the pointer between cards.
  useEffect(() => {
    const element = cardRef.current;
    if (!element) return undefined;
    return dropTargetForElements({
      element,
      getData: () => ({ columnKey, index, element }),
      canDrop: ({ source }) => source.data?.type === DRAG_TYPE,
    });
  }, [columnKey, index]);

  const rowSx = { mt: 0.75, columnGap: 1.5, rowGap: 0.5 };

  return (
    <Box
      ref={cardRef}
      onClick={() => onOpen(deal)}
      sx={{
        display: hidden ? "none" : undefined,
        flexShrink: 0,
        backgroundColor: theme.palette.background.paper,
        padding: "10px 12px 10px 14px",
        borderRadius: "10px",
        borderLeft: `3px solid ${accent}`,
        boxShadow: "0 1px 2px 0 rgba(17, 12, 34, 0.06)",
        cursor: canDrag ? "grab" : "pointer",
        opacity: isDragging || pending ? 0.45 : 1,
        transition: "box-shadow 0.2s, opacity 0.2s",
        "&:hover": { boxShadow: "0 6px 16px -6px rgba(17, 12, 34, 0.18)" },
      }}
    >
      <Typography
        sx={{
          fontSize: "0.875rem",
          fontWeight: 600,
          color: "text.primary",
          lineHeight: 1.3,
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
        }}
      >
        {deal.name || "Sin título"}
      </Typography>

      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" sx={rowSx}>
        {deal.owner_name ? (
          <NameRow name={deal.owner_name} picture={deal.owner_picture} />
        ) : (
          <Typography sx={{ ...metaTextSx, flex: "1 1 120px" }}>Sin propietario</Typography>
        )}
        {deal.expected_closed_date ? (
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
            <Calendar size={12} color={isExpired ? theme.palette.error.main : theme.palette.text.secondary} />
            <Typography
              sx={{
                ...metaTextSx,
                overflow: "visible",
                color: isExpired ? "error.main" : "text.secondary",
                fontWeight: isExpired ? 600 : 400,
              }}
            >
              {formatDate(deal.expected_closed_date)}
            </Typography>
          </Stack>
        ) : null}
      </Stack>

      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" sx={rowSx}>
        <Typography sx={{ ...metaTextSx, minWidth: 0, flex: "1 1 120px" }}>
          {contactLine || "Sin contacto"}
        </Typography>
        <Typography
          sx={{
            fontSize: "0.75rem",
            fontWeight: 600,
            flexShrink: 0,
            color: hasAmount ? "text.primary" : "text.disabled",
            fontStyle: hasAmount ? "normal" : "italic",
          }}
        >
          {hasAmount ? formatAmount(parsedAmount, deal.currency) : "Sin valor"}
        </Typography>
      </Stack>
    </Box>
  );
};

// Componente de skeleton para columnas mientras carga
const KanbanColumnSkeleton = () => {
  return (
    <Box
      sx={{
        backgroundColor: KANBAN_COLUMN_BG,
        borderRadius: "16px",
        padding: "14px",
        minHeight: "240px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5, px: 0.5 }}>
        <Skeleton variant="text" width="60%" height={24} />
        <Skeleton variant="rounded" width={40} height={24} />
      </Stack>
      <Stack spacing={1}>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rounded" height={92} sx={{ borderRadius: "10px", bgcolor: "background.paper" }} />
        ))}
      </Stack>
    </Box>
  );
};

// Skeletons de tarjetas dentro de cada columna
const KanbanCardsSkeleton = ({ count = 3 }) => (
  <Stack spacing={1}>
    {Array.from({ length: count }, (_, index) => index + 1).map((i) => (
      <Skeleton
        key={i}
        variant="rounded"
        height={92}
        sx={{ borderRadius: "10px", bgcolor: "background.paper", flexShrink: 0 }}
      />
    ))}
  </Stack>
);

/**
 * One board column: a pipeline stage, or the Ganadas / Perdidas status
 * columns. Pages are fetched per column; deals moved on the board live in
 * `overrides` until the next refetch, so a drop is shown immediately.
 */
const KanbanColumn = ({
  column,
  pipelineId,
  searchTerm,
  selectedFilter,
  orderBy,
  shouldFetch = true,
  ownerId,
  refreshKey,
  overrides,
  pendingIds,
  drag,
  hover,
  onOpenDeal,
  onCreateDeal,
}) => {
  const theme = useTheme();
  const colors = column.colors;
  const { interceptorReady } = useAuth();
  const [page, setPage] = useState(1);
  const [deals, setDeals] = useState([]);
  const [hasMoreDeals, setHasMoreDeals] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [isDraggedOver, setIsDraggedOver] = useState(false);
  const columnRef = useRef(null);
  const loadMoreRef = useRef(null);
  const [isEndVisible, setIsEndVisible] = useState(false);
  const isPagingRef = useRef(false);
  const visibleCountRef = useRef(0);

  const endpoint = useMemo(() => {
    if (!shouldFetch) return null;
    if (!column.stageId && !pipelineId) return null;

    const params = new URLSearchParams();
    if (column.stageId) {
      params.append("pipeline_stage_id", column.stageId);
      const statusValue = STATUS_MAP[selectedFilter];
      if (statusValue) params.append("status", statusValue);
    } else {
      params.append("pipeline_id", pipelineId);
      params.append("status", column.status);
    }
    params.append("page_size", DEALS_PAGE_SIZE.toString());
    params.append("page", page.toString());

    if (searchTerm && searchTerm.trim()) {
      params.append("search", searchTerm.trim());
    }
    if (orderBy) {
      params.append("order_by", orderBy);
    }
    if (ownerId) {
      params.append("owner_id", String(ownerId));
    }

    return `${CLIENTIFY_DEALS_ENDPOINT}?${params.toString()}`;
  }, [column, orderBy, ownerId, page, pipelineId, searchTerm, selectedFilter, shouldFetch]);

  const {
    data: dealsData,
    isLoading,
    isInitialLoading,
    isFetching,
    error,
  } = getData(endpoint, interceptorReady && Boolean(endpoint), [refreshKey]);

  useEffect(() => {
    setPage(1);
    setDeals([]);
    setHasMoreDeals(Boolean(shouldFetch));
    setTotalCount(0);
    setTotalAmount(0);
    isPagingRef.current = false;
  }, [shouldFetch, column.key, searchTerm, selectedFilter, orderBy, ownerId, refreshKey]);

  useEffect(() => {
    if (!shouldFetch || !dealsData) return;

    const pageResults = Array.isArray(dealsData?.results) ? dealsData.results : [];
    setDeals((prev) => {
      if (page === 1) {
        return pageResults;
      }

      const seen = new Set(prev.map((deal) => deal.id));
      const merged = pageResults.filter((deal) => !seen.has(deal.id));
      return [...prev, ...merged];
    });

    setHasMoreDeals(Boolean(dealsData?.next));
    setTotalCount(Number.parseInt(dealsData?.count, 10) || 0);
    setTotalAmount(parseNumericAmount(dealsData?.total_amount));
  }, [dealsData, page, shouldFetch]);

  useEffect(() => {
    if (!isFetching) {
      isPagingRef.current = false;
    }
  }, [isFetching]);

  // Closed deals cannot be reopened from here, so only open ones are
  // accepted, and never on the column they already sit in.
  useEffect(() => {
    const element = columnRef.current;
    if (!element) return undefined;

    return dropTargetForElements({
      element,
      getData: () => ({ columnKey: column.key, count: visibleCountRef.current }),
      canDrop: ({ source }) =>
        source.data?.type === DRAG_TYPE && source.data?.columnKey !== column.key,
      onDragEnter: () => setIsDraggedOver(true),
      onDragLeave: () => setIsDraggedOver(false),
      onDrop: () => setIsDraggedOver(false),
    });
  }, [column.key]);

  // Apply moves made on the board since the last fetch.
  const { visibleDeals, countDelta, amountDelta } = useMemo(() => {
    let count = 0;
    let amount = 0;
    const fetchedIds = new Set(deals.map((deal) => deal.id));
    const kept = [];
    deals.forEach((deal) => {
      const override = overrides[deal.id];
      if (override && override.columnKey !== column.key) {
        count -= 1;
        amount -= parseNumericAmount(deal.amount);
        return;
      }
      kept.push(override ? override.deal : deal);
    });
    const incoming = Object.values(overrides).filter(
      (override) => override.columnKey === column.key && !fetchedIds.has(override.deal.id)
    );
    const visible = [...kept];
    // Dropped cards keep the slot they were dropped on; the rest go on top.
    [...incoming]
      .sort((a, b) => (a.index ?? -1) - (b.index ?? -1))
      .forEach((override) => {
        count += 1;
        amount += parseNumericAmount(override.deal.amount);
        const at = Math.min(Math.max(override.index ?? 0, 0), visible.length);
        visible.splice(at, 0, override.deal);
      });
    return { visibleDeals: visible, countDelta: count, amountDelta: amount };
  }, [deals, overrides, column.key]);

  const loadNextPage = useCallback(() => {
    if (!shouldFetch) return;
    if (!hasMoreDeals) return;
    if (isPagingRef.current) return;
    if (isFetching || isLoading || isInitialLoading || error) return;

    isPagingRef.current = true;
    setPage((prev) => prev + 1);
  }, [error, hasMoreDeals, isFetching, isInitialLoading, isLoading, shouldFetch]);

  // Scroll infinito con el de la página: cuando el final de la columna entra en
  // pantalla se pide la siguiente página, y se sigue pidiendo mientras siga visible.
  useEffect(() => {
    const element = loadMoreRef.current;
    if (!element) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setIsEndVisible(entry.isIntersecting),
      { rootMargin: "300px 0px" }
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isEndVisible && deals.length > 0) loadNextPage();
  }, [isEndVisible, deals.length, isFetching, loadNextPage]);

  // The dragged card leaves its column as soon as it hovers another one, and
  // the hovered column opens a slot of its height where it would land.
  const isSourceColumn = drag?.columnKey === column.key;
  const hoveringElsewhere = Boolean(hover && hover.columnKey !== drag?.columnKey);
  const hideDragged = isSourceColumn && hoveringElsewhere;
  const cards = visibleDeals;
  visibleCountRef.current = cards.length;
  const slotIndex =
    hover?.columnKey === column.key && !isSourceColumn
      ? Math.min(hover.index, cards.length)
      : null;
  const placeholder =
    slotIndex === null ? null : (
      <Box
        key="__drop-slot__"
        sx={{
          height: drag?.height || 120,
          flexShrink: 0,
          borderRadius: "10px",
          border: `2px dashed ${colors.color}`,
          backgroundColor: alpha(colors.color, 0.08),
          animation: `${placeholderGrow} 0.18s ease-out`,
          overflow: "hidden",
        }}
      />
    );

  const showLoading = shouldFetch && page === 1 && (isLoading || isInitialLoading);
  const showError = shouldFetch && page === 1 && error;
  const showLoadingMore = shouldFetch && page > 1 && isFetching;
  const showLoadMoreError = shouldFetch && page > 1 && error;
  const columnCurrency = visibleDeals[0]?.currency || "EUR";
  const formattedTotalAmount = formatAmount(totalAmount + amountDelta, columnCurrency);

  return (
    <Box
      ref={columnRef}
      sx={{
        backgroundColor: KANBAN_COLUMN_BG,
        borderRadius: "16px",
        outline: isDraggedOver ? `2px dashed ${colors.color}` : "2px dashed transparent",
        outlineOffset: "-2px",
        padding: "14px",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        transition: "outline-color 0.15s",
      }}
    >
      {/* Header de la columna: puntito de color + título + contador, e importe a la derecha */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5, px: 0.5, gap: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
          <Box
            sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: colors.color, flexShrink: 0 }}
          />
          <Typography
            sx={{
              fontSize: "0.95rem",
              fontWeight: 700,
              color: "text.primary",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {column.title}
          </Typography>
          <Typography sx={{ fontSize: "0.875rem", color: "text.secondary", flexShrink: 0 }}>
            {showLoading ? "…" : Math.max(0, totalCount + countDelta)}
          </Typography>
          {column.stageId && (
            <Tooltip title="Agregar oportunidad">
              <IconButton
                size="small"
                onClick={() => onCreateDeal(column.stageId)}
                sx={{ color: "text.secondary", p: 0.25, "&:hover": { color: colors.color } }}
              >
                <Plus size={16} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>

        <Typography
          sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "text.primary", whiteSpace: "nowrap" }}
        >
          {showLoading ? "…" : formattedTotalAmount}
        </Typography>
      </Stack>

      {/* Tarjetas de oportunidades */}
      <Stack spacing={1}>
        {showLoading ? (
          <KanbanCardsSkeleton />
        ) : showError ? (
          <Box sx={{ textAlign: "center", py: 4, color: theme.palette.error.main }}>
            <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
              Error al cargar
            </Typography>
          </Box>
        ) : cards.length === 0 && !placeholder ? (
          <Box sx={{ textAlign: "center", py: 4, color: theme.palette.text.secondary }}>
            <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
              No hay oportunidades
            </Typography>
          </Box>
        ) : (
          <>
            {cards.flatMap((deal, index) => {
              const card = (
                <OpportunityCard
                  key={deal.id}
                  deal={deal}
                  columnKey={column.key}
                  index={index}
                  accent={colors.color}
                  onOpen={onOpenDeal}
                  pending={pendingIds.has(deal.id)}
                  // Hidden, not unmounted: the browser drag needs its source element.
                  hidden={hideDragged && deal.id === drag.deal.id}
                />
              );
              return index === slotIndex ? [placeholder, card] : [card];
            })}
            {slotIndex !== null && slotIndex >= cards.length && placeholder}

            {showLoadMoreError && (
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.8125rem",
                  color: theme.palette.error.main,
                  textAlign: "center",
                  py: 1,
                }}
              >
                Error al cargar más oportunidades
              </Typography>
            )}

            {showLoadingMore && <KanbanCardsSkeleton count={2} />}
          </>
        )}
      </Stack>
      <Box ref={loadMoreRef} sx={{ height: 1 }} />
    </Box>
  );
};

/**
 * Deals board. Dragging a card onto another stage moves it in Clientify;
 * dropping it on Ganadas / Perdidas asks for the closing details first.
 *
 * `overrides` / `onDealMoved` are owned by the parent so that edits made in
 * the side panel land on the board the same way as a drop.
 */
export const VistaKanban = ({
  stages,
  stagesLoading,
  stagesError,
  searchTerm,
  selectedFilter,
  ownerId,
  orderBy,
  refreshKey,
  overrides,
  onDealMoved,
  onOpenDeal,
  onCreateDeal,
  onRequestClose,
}) => {
  const [pendingIds, setPendingIds] = useState(() => new Set());
  const [drag, setDrag] = useState(null);
  const [hover, setHover] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: "", error: false });
  const hoverRef = useRef(null);
  // State (not a ref) so the effect below re-runs once the board mounts:
  // the first render is usually the loading skeleton, with no board element.
  const [boardScrollElement, setBoardScrollElement] = useState(null);
  // Columnas como mínimo hasta el final de la ventana; crecen con sus tarjetas (scroll de página)
  const fillHeight = useViewportBottomHeight(boardScrollElement, KANBAN_BOTTOM_GAP);

  // El tablero puede ser más ancho que la pantalla; al arrastrar una
  // tarjeta hasta el borde, hace autoscroll lateral en vez de quedarse
  // atascado fuera de la vista. La velocidad crece al acercarse al borde
  // (zona de hasta 180px) y se detiene al soltar o salir de la zona.
  // Si la ventana es la que desborda, también se desplaza.
  useEffect(() => {
    const cleanups = [
      autoScrollWindowForElements({ canScroll: isDealDrag }),
    ];
    if (boardScrollElement) {
      cleanups.push(
        autoScrollForElements({
          element: boardScrollElement,
          canScroll: isDealDrag,
          getAllowedAxis: () => "horizontal",
          getConfiguration: () => ({ maxScrollSpeed: "fast" }),
        })
      );
    }
    return combine(...cleanups);
  }, [boardScrollElement]);

  // Obtener el pipeline_id del primer stage (todos pertenecen al mismo pipeline)
  const pipelineId = stages.length > 0 ? stages[0].pipeline_id : null;

  const columns = useMemo(() => {
    const stageColumns = stages.map((stage) => ({
      key: `stage:${stage.id}`,
      title: stage.name,
      stageId: stage.id,
      colors: getColorFromPosition(stage.position),
    }));
    if (!pipelineId) return stageColumns;
    return [
      ...stageColumns,
      { key: "won", title: "Ganadas", status: DEAL_STATUS.won, colors: kanbanColors.columns.ganada },
      { key: "lost", title: "Perdidas", status: DEAL_STATUS.lost, colors: kanbanColors.columns.perdida },
    ];
  }, [stages, pipelineId]);

  const stagesById = useMemo(
    () => Object.fromEntries(stages.map((stage) => [String(stage.id), stage])),
    [stages]
  );

  const moveToStage = useCallback(
    async (deal, stageId, index) => {
      const stage = stagesById[String(stageId)];
      onDealMoved({ ...deal, pipeline_stage_id: stageId, pipeline_stage_desc: stage?.name }, index);
      setPendingIds((prev) => new Set(prev).add(deal.id));
      try {
        const saved = await updateClientifyDeal(deal.id, { pipeline_stage_id: stageId });
        onDealMoved({ ...deal, ...saved }, index);
        setNotification({
          open: true,
          message: `Oportunidad movida a ${stage?.name || "la nueva etapa"}`,
          error: false,
        });
      } catch (error) {
        onDealMoved(deal);
        setNotification({
          open: true,
          message: clientifyErrorMessage(error, "No se pudo mover la oportunidad"),
          error: true,
        });
      } finally {
        setPendingIds((prev) => {
          const next = new Set(prev);
          next.delete(deal.id);
          return next;
        });
      }
    },
    [onDealMoved, stagesById]
  );

  useEffect(
    () =>
      monitorForElements({
        canMonitor: ({ source }) => source.data?.type === DRAG_TYPE,
        onDragStart: ({ source }) => {
          setDrag({
            deal: source.data.deal,
            columnKey: source.data.columnKey,
            height: source.element.getBoundingClientRect().height,
          });
        },
        onDrag: ({ location }) => {
          const next = hoverFromLocation(location);
          const prev = hoverRef.current;
          // Pointer on the open slot reads as "column": keep the slot where it is,
          // or it would jump to the end and back on every frame.
          if (next?.overColumn && prev?.columnKey === next.columnKey) return;
          if (prev?.columnKey === next?.columnKey && prev?.index === next?.index) return;
          hoverRef.current = next;
          setHover(next);
        },
        onDrop: ({ source, location }) => {
          const target = location.current.dropTargets.length ? hoverRef.current : null;
          hoverRef.current = null;
          setDrag(null);
          setHover(null);
          const deal = source.data?.deal;
          if (!target || !deal || target.columnKey === source.data?.columnKey) return;

          if (target.columnKey === "won" || target.columnKey === "lost") {
            onRequestClose(deal, target.columnKey);
            return;
          }
          moveToStage(deal, Number(target.columnKey.replace("stage:", "")), target.index);
        },
      }),
    [moveToStage, onRequestClose]
  );

  // Definir qué columnas hacen fetch según el tab activo
  const shouldFetchColumn = (column) => {
    if (column.stageId) {
      return ["all", "open", "overdue"].includes(selectedFilter);
    }
    return selectedFilter === "all" || selectedFilter === column.key;
  };

  if (stagesLoading) {
    return (
      <Box sx={{ width: "100%", overflowX: "auto", overflowY: "hidden", pb: 0.5 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(4, minmax(300px, 1fr))",
            gap: 2,
            minWidth: "fit-content",
          }}
        >
          {[1, 2, 3, 4].map((i) => (
            <KanbanColumnSkeleton key={i} />
          ))}
        </Box>
      </Box>
    );
  }

  if (stagesError) {
    return (
      <Box sx={{ py: 8 }}>
        <EmptyStateMessage
          message="Error al cargar las etapas del pipeline. Por favor, intenta nuevamente."
          iconSize={48}
        />
      </Box>
    );
  }

  if (!stages || stages.length === 0) {
    return (
      <Box sx={{ py: 8 }}>
        <EmptyStateMessage icon={Inbox} message="No se encontraron etapas en el pipeline." iconSize={48} />
      </Box>
    );
  }

  return (
    <Box
      ref={setBoardScrollElement}
      sx={{ width: "100%", overflowX: "auto", overflowY: "hidden", pb: 0.5, mb: `${KANBAN_BOTTOM_GAP}px` }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: `repeat(${columns.length}, minmax(300px, 1fr))`,
          gap: 2,
          minWidth: "fit-content",
          minHeight: fillHeight ? `max(360px, ${fillHeight})` : 360,
        }}
      >
        {columns.map((column) => (
          <KanbanColumn
            key={column.key}
            column={column}
            pipelineId={pipelineId}
            searchTerm={searchTerm}
            selectedFilter={selectedFilter}
            orderBy={orderBy}
            shouldFetch={shouldFetchColumn(column)}
            ownerId={ownerId}
            refreshKey={refreshKey}
            overrides={overrides}
            pendingIds={pendingIds}
            drag={drag}
            hover={hover}
            onOpenDeal={onOpenDeal}
            onCreateDeal={onCreateDeal}
          />
        ))}
      </Box>
      <SuccessPopup
        message={notification.message}
        isOpen={notification.open}
        error={notification.error}
        onClose={() => setNotification({ open: false, message: "", error: false })}
      />
    </Box>
  );
};

NameRow.propTypes = {
  name: PropTypes.string,
  picture: PropTypes.string,
};

OpportunityCard.propTypes = {
  deal: PropTypes.object.isRequired,
  columnKey: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  accent: PropTypes.string.isRequired,
  onOpen: PropTypes.func.isRequired,
  pending: PropTypes.bool,
  hidden: PropTypes.bool,
};

KanbanCardsSkeleton.propTypes = {
  count: PropTypes.number,
};

KanbanColumn.propTypes = {
  column: PropTypes.shape({
    key: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    stageId: PropTypes.number,
    status: PropTypes.string,
    colors: PropTypes.shape({
      color: PropTypes.string.isRequired,
      bgColor: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  pipelineId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  searchTerm: PropTypes.string,
  selectedFilter: PropTypes.string,
  orderBy: PropTypes.string,
  shouldFetch: PropTypes.bool,
  ownerId: PropTypes.number,
  refreshKey: PropTypes.number,
  overrides: PropTypes.object.isRequired,
  pendingIds: PropTypes.instanceOf(Set).isRequired,
  drag: PropTypes.object,
  hover: PropTypes.object,
  onOpenDeal: PropTypes.func.isRequired,
  onCreateDeal: PropTypes.func.isRequired,
};

VistaKanban.propTypes = {
  stages: PropTypes.arrayOf(PropTypes.object).isRequired,
  stagesLoading: PropTypes.bool,
  stagesError: PropTypes.object,
  searchTerm: PropTypes.string,
  selectedFilter: PropTypes.string,
  ownerId: PropTypes.number,
  orderBy: PropTypes.string,
  refreshKey: PropTypes.number,
  overrides: PropTypes.object.isRequired,
  onDealMoved: PropTypes.func.isRequired,
  onOpenDeal: PropTypes.func.isRequired,
  onCreateDeal: PropTypes.func.isRequired,
  onRequestClose: PropTypes.func.isRequired,
};
